<template>
  <section class="page-stack combat-logs-view">
    <article class="card page-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Combat Logs</p>
          <h2>Log analyzer</h2>
          <p>
            Damage, healing, defensives and deaths from your own combat logs. Connect the folder once and every new log is
            read locally, incrementally and without uploading anything.
          </p>
        </div>
      </header>
    </article>

    <!-- Restoring the folder, the stored analysis and the last fight takes a
         moment, and each step would otherwise land on screen on its own. The
         view is held behind one loader until all of it is ready. -->
    <article v-if="opening" class="card cl-placeholder cl-opening">
      <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
      <h3>{{ openingStep }}</h3>
      <p class="muted-line">Reading the folder and the stored analysis. Nothing is uploaded.</p>
    </article>

    <template v-else>
    <div v-if="error" class="card cl-banner is-error">
      <span><i class="fa-solid fa-triangle-exclamation"></i> {{ error }}</span>
      <button class="btn-sm-outline" type="button" @click="dismissError">Dismiss</button>
    </div>
    <div v-if="notice" class="card cl-banner is-notice">
      <span><i class="fa-solid fa-circle-info"></i> {{ notice }}</span>
      <button class="btn-sm-outline" type="button" @click="dismissNotice">Dismiss</button>
    </div>

    <CombatLogStatusBar
      :supported="supported.directory"
      :directory="directory"
      :monitor="monitor"
      :monitor-enabled="monitorEnabled"
      :monitor-label="monitorLabel"
      :files="visibleFiles"
      :selected-file-id="selectedFileId"
      :segment-options="segmentOptions"
      :selected-segment-key="selectedSegmentKey"
      :can-export="!!report"
      :progress="selectedProgress"
      @connect="connectDirectory"
      @request-access="requestDirectoryAccess"
      @refresh="refresh"
      @pause="pauseMonitoring"
      @resume="resumeMonitoring"
      @files="addManualFiles"
      @select="selectFile"
      @select-segment="selectSegment"
      @export="exportReport"
    />

    <article v-if="!selectedFile" class="card cl-placeholder">
      <i class="fa-solid fa-chart-line" aria-hidden="true"></i>
      <h3>No log selected</h3>
      <p class="muted-line">
        Connect your Logs folder or drop a .txt log on the bar above. New logs appear on their own while the folder is
        monitored.
      </p>
      <button v-if="supported.directory && !directory.connected" class="btn" type="button" @click="connectDirectory">
        <i class="fa-solid fa-folder-open"></i>
        Choose folder
      </button>
    </article>

    <!-- A readable log is never offered for analysis, it is simply analyzed,
         so this card only ever waits or explains why it cannot. -->
    <article v-else-if="!report" class="card cl-placeholder">
      <i
        class="fa-solid"
        :class="selectedFile.available ? 'fa-spinner fa-spin' : 'fa-file-circle-question'"
        aria-hidden="true"
      ></i>
      <h3>{{ selectedFile.available ? 'Analyzing ' + selectedFile.name : 'This log cannot be read' }}</h3>
      <p class="muted-line">
        {{ selectedFile.available
          ? 'Reading in the background; results appear as soon as the first pass finishes. Only the bytes that are new are ever read.'
          : 'The file is no longer in the folder, or the page has lost access to it. Rescan the folder or pick it again.' }}
      </p>
    </article>

    <template v-else>
      <section class="mode-switch-panel cl-mode-switch" role="tablist" aria-label="Analysis section">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          role="tab"
          :class="{ 'is-active': activeTab === tab.key }"
          :aria-selected="activeTab === tab.key"
          @click="setActiveTab(tab.key)"
        >
          <i class="fa-solid" :class="tab.icon"></i>
          <span>
            <strong>{{ tab.label }}</strong>
            <small>{{ tab.hint }}</small>
          </span>
        </button>
      </section>

      <CombatLogChartPanel
        v-if="activeTab === 'overview' || activeTab === 'players'"
        :report="report"
        :range="selection"
        :player-guid="selectedPlayerGuid"
        @select-range="selection = $event"
      />

      <CombatLogOverview
        v-if="activeTab === 'overview'"
        :report="report"
        :summary="summary"
        :selection="selection"
        @select-player="pickPlayer"
      />

      <CombatLogEncounters
        v-if="activeTab === 'overview'"
        :summary="summary"
        :selected-segment-key="selectedSegmentKey"
        :dungeon-index="dungeonIndex"
        @select-segment="selectSegment"
      />

      <CombatLogRaiderLink
        v-if="activeTab === 'overview' && challengeSegments.length"
        :log-owner="summary.logOwner"
        :log-runs="challengeSegments"
        :saved-characters="favorites"
        :linked-character-id="linkedCharacterId"
        @link="setLinkedCharacter"
        @unlink="setLinkedCharacter('')"
      />

      <CombatLogPlayers
        v-if="activeTab === 'players'"
        :report="report"
        :selected-player-guid="selectedPlayerGuid"
        :show-non-group-players="showNonGroupPlayers"
        @select-player="setSelectedPlayer"
        @toggle-non-group="setShowNonGroupPlayers"
      />

      <CombatLogAbilities
        v-if="activeTab === 'abilities'"
        :report="report"
      />

      <CombatLogDeaths
        v-if="activeTab === 'deaths'"
        :deaths="report.deaths"
        :window-seconds="deathWindowSeconds"
        @set-window="setDeathWindow"
      />

      <CombatLogCompare
        v-if="activeTab === 'compare'"
        :report="report"
        :summary="summary"
        :runs="runCatalog"
        :compare-key="compareKey"
        :other="compareReport"
        :loading="compareLoading"
        :current-key="currentRunKey"
        @select="selectCompareRun"
      />
    </template>

    <!-- The log list and the folder itself are a section like any other. With
         nothing analyzed yet they are shown straight away, since that is where
         a log is chosen or files are picked by hand. -->
    <template v-if="!report || activeTab === 'logs'">
      <CombatLogFileList
        :files="visibleFiles"
        :selected-file-id="selectedFileId"
        :sort-key="sortKey"
        :sort-direction="sortDirection"
        :progress="progress"
        :only-wow-logs="onlyWowLogs"
        :auto-analyze-newest="autoAnalyzeNewest"
        :storage="storage"
        :report-cache="reportCache"
        :report-cache-mb="reportCacheMb"
        @select="selectFile"
        @analyze="analyzeFile({ fileId: $event })"
        @reanalyze="reanalyzeFile"
        @cancel="cancelAnalysis"
        @remove="removeFileAnalysis"
        @sort="setSort"
        @toggle-only-wow-logs="setOnlyWowLogs"
        @toggle-auto-analyze="setAutoAnalyzeNewest"
        @clear-stored="confirmClearStored"
        @set-cache-budget="setReportCacheMb"
        @clear-cache="clearReportCache"
      />

      <CombatLogConnection
        :supported="supported.directory"
        :observer-supported="supported.observer"
        :uses-worker="usesWorker"
        :directory="directory"
        :monitor-label="monitorLabel"
        :scan-info="scanInfo"
        :files="visibleFiles"
        @connect="connectDirectory"
        @disconnect="confirmDisconnect"
        @files="addManualFiles"
      />
    </template>
    </template>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import CombatLogStatusBar from '../components/combatLog/CombatLogStatusBar.vue'
import CombatLogFileList from '../components/combatLog/CombatLogFileList.vue'
import CombatLogConnection from '../components/combatLog/CombatLogConnection.vue'
import CombatLogChartPanel from '../components/combatLog/CombatLogChartPanel.vue'
import CombatLogOverview from '../components/combatLog/CombatLogOverview.vue'
import CombatLogPlayers from '../components/combatLog/CombatLogPlayers.vue'
import CombatLogEncounters from '../components/combatLog/CombatLogEncounters.vue'
import CombatLogAbilities from '../components/combatLog/CombatLogAbilities.vue'
import CombatLogDeaths from '../components/combatLog/CombatLogDeaths.vue'
import CombatLogCompare from '../components/combatLog/CombatLogCompare.vue'
import CombatLogRaiderLink from '../components/combatLog/CombatLogRaiderLink.vue'
import { loadDungeonIndex } from '../services/combatLog/raiderMatch'
import { formatClock, formatDuration, formatSize, plural } from '../services/combatLog/format'
import { buildReportExport, downloadJson, exportFileName } from '../services/combatLog/exporter'

export default {
  name: 'CombatLogsView',
  components: {
    CombatLogStatusBar,
    CombatLogFileList,
    CombatLogConnection,
    CombatLogChartPanel,
    CombatLogOverview,
    CombatLogPlayers,
    CombatLogEncounters,
    CombatLogAbilities,
    CombatLogDeaths,
    CombatLogCompare,
    CombatLogRaiderLink,
  },
  data() {
    return {
      // Zoom range and the log drawer are view state on purpose: a background
      // refresh of the analysis must not throw away what the user opened.
      selection: null,
      dungeonIndex: null,
      // Held until the folder, the stored analysis and the fight are all in
      // place, and for a moment longer, so nothing flashes on its way there.
      opening: true,
      openingStep: 'Opening your logs',
      openingTimer: null,
    }
  },
  computed: {
    ...mapState('combatLogs', [
      'supported',
      'usesWorker',
      'directory',
      'monitor',
      'monitorEnabled',
      'scanInfo',
      'selectedFileId',
      'selectedSegmentKey',
      'selectedPlayerGuid',
      'activeTab',
      'sortKey',
      'sortDirection',
      'onlyWowLogs',
      'showNonGroupPlayers',
      'autoAnalyzeNewest',
      'deathWindowSeconds',
      'linkedCharacterId',
      'progress',
      'processingFileId',
      'storage',
      'lastUpdateAt',
      'reportCache',
      'reportCacheMb',
      'runCatalog',
      'compareKey',
      'compareReport',
      'compareLoading',
      'error',
      'notice',
    ]),
    ...mapGetters('combatLogs', [
      'visibleFiles',
      'selectedFile',
      'selectedSummary',
      'selectedReport',
      'selectedProgress',
      'monitorLabel',
    ]),
    favorites() {
      return this.$store.state.favorites || []
    },
    summary() {
      return this.selectedSummary
    },
    report() {
      return this.selectedReport
    },
    isSelectedProcessing() {
      return this.processingFileId === this.selectedFileId
    },
    challengeSegments() {
      if (!this.summary) return []
      return (this.summary.segments || []).filter((segment) => segment.type === 'challenge')
    },
    // The fight is picked in the scope dropdown, so there is no tab for it; its
    // run list, boss results and Raider.IO link live at the end of Overview.
    //
    // These feed the application's own mode switch, whose buttons carry a line
    // of explanation, so the counts are written into that line instead of into
    // a badge of their own.
    tabs() {
      const report = this.report
      return [
        { key: 'overview', label: 'Overview', icon: 'fa-gauge-high', hint: 'Totals and timeline' },
        {
          key: 'players',
          label: 'Players',
          icon: 'fa-users',
          hint: report ? `${plural(report.players.length, 'character')} in this fight` : 'Per character output',
        },
        { key: 'abilities', label: 'Abilities', icon: 'fa-wand-sparkles', hint: 'Damage and healing by spell' },
        {
          key: 'deaths',
          label: 'Deaths',
          icon: 'fa-skull',
          hint: report ? plural(report.deaths.length, 'player death') : 'What happened before each death',
        },
        { key: 'compare', label: 'Compare', icon: 'fa-scale-balanced', hint: 'Against another run' },
        {
          key: 'logs',
          label: 'Logs',
          icon: 'fa-folder-tree',
          hint: `${plural(this.visibleFiles.length, 'log')} and the folder`,
        },
      ]
    },
    currentRunKey() {
      return `${this.selectedFileId}::${this.selectedSegmentKey}`
    },
    // Newest first, the way every other list of logs and runs in the app reads:
    // the fight that just ended is at the top, with the whole log above them
    // all because it is not one of them.
    segmentOptions() {
      if (!this.summary) return []
      const segments = [...(this.summary.segments || [])].sort((left, right) => {
        if (left.type === 'log') return -1
        if (right.type === 'log') return 1
        return right.startTs - left.startTs
      })

      return segments.map((segment) => {
        if (segment.type === 'log') {
          return {
            value: segment.key,
            label: 'Whole log',
            icon: 'fa-file-lines',
            hint: `${segment.playerCount} players - ${plural(segment.deathCount, 'death')}`,
            meta: formatDuration(segment.durationMs),
          }
        }

        const level = segment.meta && segment.meta.keystoneLevel ? ` +${segment.meta.keystoneLevel}` : ''
        const result = segment.meta && segment.meta.success === true
          ? 'Kill'
          : (segment.meta && segment.meta.success === false ? 'Wipe' : '')
        const hint = [
          segment.startTs ? formatClock(segment.startTs) : '',
          result,
          plural(segment.deathCount, 'death'),
        ].filter(Boolean).join(' - ')

        return {
          value: segment.key,
          label: `${segment.name}${level}`,
          icon: segment.type === 'challenge' ? 'fa-dungeon' : 'fa-dragon',
          hint,
          meta: formatDuration(segment.fightMs || segment.durationMs),
        }
      })
    },
  },
  watch: {
    selectedSegmentKey() {
      this.selection = null
      this.ensureReport()
    },
    selectedFileId() {
      this.selection = null
      this.ensureReport()
    },
    // The catalog is built from the stored summaries, so it only changes when
    // an analysis finishes.
    lastUpdateAt() {
      this.loadRunCatalog()
    },
    activeTab(tab) {
      if (tab === 'compare') this.loadRunCatalog()
    },
  },
  async created() {
    // Two seconds is the floor rather than the cost: on a warm cache the work
    // below finishes sooner, and the loader stays up for the rest of it so the
    // view appears once, already settled.
    const startedAt = Date.now()
    try {
      this.openingStep = 'Reading the folder'
      await this.initialize()
      this.openingStep = 'Opening the last fight'
      await this.ensureReport()
    } finally {
      const left = Math.max(0, 2000 - (Date.now() - startedAt))
      this.openingTimer = setTimeout(() => { this.opening = false }, left)
    }

    this.refreshStorageUsage()
    this.refreshReportCache()
    this.loadDungeons()
  },
  beforeUnmount() {
    if (this.openingTimer) clearTimeout(this.openingTimer)
  },
  methods: {
    ...mapActions('combatLogs', [
      'initialize',
      'connectDirectory',
      'requestDirectoryAccess',
      'disconnectDirectory',
      'refresh',
      'pauseMonitoring',
      'resumeMonitoring',
      'selectFile',
      'selectSegment',
      'analyzeFile',
      'ensureReport',
      'reanalyzeFile',
      'cancelAnalysis',
      'removeFileAnalysis',
      'clearStoredAnalyses',
      'addManualFiles',
      'refreshStorageUsage',
      'refreshReportCache',
      'setReportCacheMb',
      'clearReportCache',
      'loadRunCatalog',
      'selectCompareRun',
      'setSort',
      'setActiveTab',
      'setSelectedPlayer',
      'setOnlyWowLogs',
      'setShowNonGroupPlayers',
      'setAutoAnalyzeNewest',
      'setDeathWindow',
      'setLinkedCharacter',
      'dismissNotice',
      'dismissError',
    ]),
    async loadDungeons() {
      try {
        const index = await loadDungeonIndex(11)
        this.dungeonIndex = Object.fromEntries(index)
      } catch (error) {
        // Dungeon names and timers are a convenience; the analysis does not
        // depend on them.
        this.dungeonIndex = null
      }
    },
    exportReport() {
      if (!this.report) return
      const payload = buildReportExport({
        report: this.report,
        summary: this.summary,
        file: this.selectedFile,
      })
      const size = downloadJson(exportFileName(this.report, this.summary), payload)
      this.$store.commit('combatLogs/setNotice', `Exported ${formatSize(size)} of JSON. Nothing left this computer.`)
    },
    pickPlayer(guid) {
      this.setSelectedPlayer(guid)
      this.setActiveTab('players')
    },
    confirmDisconnect() {
      this.$store.dispatch('requestConfirmation', {
        title: 'Disconnect the log folder?',
        message: 'Access to the folder is released. Stored analyses stay available and the folder can be reconnected at any time.',
        confirmLabel: 'Disconnect',
        action: 'combatLogs/disconnectDirectory',
      })
    },
    confirmClearStored() {
      this.$store.dispatch('requestConfirmation', {
        title: 'Clear stored analyses?',
        message: 'Every parsed result, resume point and cached report is deleted. The log files themselves are untouched.',
        confirmLabel: 'Clear',
        action: 'combatLogs/clearStoredAnalyses',
      })
    },
  },
}
</script>
